import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNetworkStatus } from '../../../shared/hooks/useNetworkStatus';

export interface QueuedUpload {
  id: string;
  file: File;
  styleId: string;
  queuedAt: string;
  backgroundColorCode?: string | null;
}

export interface UseOfflineUploadQueueOptions {
  autoFlush?: boolean;
  onFlush?: (upload: QueuedUpload) => Promise<void> | void;
}

export function useOfflineUploadQueue(options: UseOfflineUploadQueueOptions = {}) {
  const { online } = useNetworkStatus();
  const [queue, setQueue] = useState<QueuedUpload[]>([]);
  const [isFlushing, setIsFlushing] = useState(false);

  const enqueue = useCallback((file: File, styleId: string, backgroundColorCode?: string | null) => {
    const entry: QueuedUpload = {
      id: crypto.randomUUID(),
      file,
      styleId,
      queuedAt: new Date().toISOString(),
      backgroundColorCode: backgroundColorCode ?? null
    };
    setQueue((prev) => [...prev, entry]);
    return entry;
  }, []);

  const dequeue = useCallback((uploadId: string) => {
    setQueue((prev) => prev.filter((item) => item.id !== uploadId));
  }, []);

  const clear = useCallback(() => {
    setQueue([]);
  }, []);

  const flush = useCallback(async () => {
    if (!options.onFlush) return;
    setIsFlushing(true);
    try {
      for (const upload of queue) {
        await options.onFlush(upload);
        dequeue(upload.id);
      }
    } finally {
      setIsFlushing(false);
    }
  }, [queue, options.onFlush, dequeue]);

  useEffect(() => {
    if (online && queue.length && options.autoFlush && options.onFlush && !isFlushing) {
      void flush();
    }
  }, [online, queue.length, options.autoFlush, options.onFlush, flush, isFlushing]);

  return useMemo(
    () => ({ queue, enqueue, dequeue, clear, flush, isFlushing, isOffline: !online }),
    [queue, enqueue, dequeue, clear, flush, isFlushing, online]
  );
}
